import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { Form } from 'react-final-form';
import { UnderlinedButton } from 'jl-design-system/elements/button/Button';
import { AddressQuestion, ADDRESS_FIELDS as addressFields } from '../address-question';
import { errorTypes } from '~/components';
import { scrollToAnchor } from '~/utils';

import styles from './AddressForm.scss';

const { ADDRESS_NOT_ENTERED_ERROR } = errorTypes;
const addressFormName = 'AddressForm';

// Custom validation function to replace removed validate from jl-design-system
const validateAddressFields = (addressValues, fields) => {
  const errors = {};

  fields.forEach((field) => {
    const value = addressValues?.[field.name];
    const fieldErrors = [];

    // Check required validation
    if (field.required && (!value || value.trim() === '')) {
      const message =
        field.validationMessageKeys?.required || `${field.label || field.name} is required`;
      fieldErrors.push(message);
    }

    // Check regex validation
    if (value && field.regex && !field.regex.test(value)) {
      const message =
        field.validationMessageKeys?.regex || `${field.label || field.name} is invalid`;
      fieldErrors.push(message);
    }

    // Check maxLength validation
    if (value && field.maxLength && value.length > field.maxLength) {
      const message =
        field.validationMessageKeys?.maxLength || `${field.label || field.name} is too long`;
      fieldErrors.push(message);
    }

    if (fieldErrors.length > 0) {
      errors[field.name] = fieldErrors[0]; // Return first error
    }
  });

  return errors;
};

export class AddressForm extends PureComponent {
  resetAddressForm = (form) => {
    const { hideAddressFormHandler } = this.props;
    form.reset();
    hideAddressFormHandler();
  };

  validateForm = (values) => {
    const { removeSingleErrorMessage } = this.props;
    const { address } = values;

    const errors = {
      address: validateAddressFields(address, addressFields),
    };

    if (!Object.keys(errors.address).length) {
      removeSingleErrorMessage(ADDRESS_NOT_ENTERED_ERROR);
    }

    return errors;
  };

  handleSubmitFail = () => {
    const { clearMessages, resetErrorMessages } = this.props;
    clearMessages();
    resetErrorMessages([{ type: ADDRESS_NOT_ENTERED_ERROR }]);
    scrollToAnchor('address-title');
  };

  render() {
    const { initialValues, children, onSubmit } = this.props;

    const [errorMessage, formButtons] = React.Children.toArray(children);

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={this.validateForm}
        render={({ handleSubmit, form }) => (
          <form
            className={styles.AddressForm}
            data-testid={addressFormName}
            onSubmit={(event) => {
              const result = handleSubmit(event);
              if (result && typeof result.then === 'function') {
                result.catch((errors) => this.handleSubmitFail(errors));
              }
              return result;
            }}
            noValidate
          >
            <div className={styles.AddressFormErrorMessage}>{errorMessage}</div>
            <div className={styles.AddressForm_holder}>
              <AddressQuestion
                reduxFieldConfig={{ name: 'address' }}
                definition={initialValues.address}
              />
              <UnderlinedButton type="button" onClick={() => this.resetAddressForm(form)}>
                Search for another address
              </UnderlinedButton>
            </div>
            <div className={styles.AddressFormButtons}>{formButtons}</div>
          </form>
        )}
      />
    );
  }
}

export const ConnectedAddressForm = AddressForm;

AddressForm.propTypes = {
  children: PropTypes.node.isRequired,
  onSubmit: PropTypes.func.isRequired,
  hideAddressFormHandler: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({}).isRequired,
  removeSingleErrorMessage: PropTypes.func.isRequired,
  clearMessages: PropTypes.func.isRequired,
  resetErrorMessages: PropTypes.func.isRequired,
};
